import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchActiveOrganizationFeatureFlags } from '../api/organizations';
import { useAuth } from '../contexts/AuthContext';

export interface ActiveFeatureFlags {
  testcase_generation: boolean;
  test_execution: boolean;
  synthetic_data: boolean;
  byo_key: boolean;
  data_validation_agent: boolean;
  exploratory_agent: boolean;
  ritz_reservation_demo: boolean;
}

export type ServiceMaintenanceKey =
  | 'test_generation'
  | 'playwright_execution'
  | 'synthetic_data'
  | 'data_validation'
  | 'exploratory_agent'
  | 'ritz_reservation_demo'
  | 'azure_devops'
  | 'billing';

export interface ServiceMaintenanceStatus {
  maintenance_enabled: boolean;
  service_name: string;
  message: string;
}

export type ServiceMaintenanceMap = Partial<Record<ServiceMaintenanceKey, ServiceMaintenanceStatus>>;

const DEFAULT_FLAGS: ActiveFeatureFlags = {
  testcase_generation: true,
  test_execution: true,
  synthetic_data: true,
  byo_key: false,
  data_validation_agent: false,
  exploratory_agent: false,
  ritz_reservation_demo: false,
};

export function useActiveFeatureFlags(): {
  flags: ActiveFeatureFlags;
  maintenance: ServiceMaintenanceMap;
  loading: boolean;
  lastLoadedAt: number;
  refresh: () => void;
} {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const activeOrganizationId = user?.active_organization_id;
  const userId = user?.id;
  const hasUser = Boolean(user);
  const [flags, setFlags] = useState<ActiveFeatureFlags>(DEFAULT_FLAGS);
  const [maintenance, setMaintenance] = useState<ServiceMaintenanceMap>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [lastLoadedAt, setLastLoadedAt] = useState<number>(0);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const refresh = useCallback(() => setRefreshVersion((version) => version + 1), []);

  useEffect(() => {
    let mounted = true;

    if (authLoading) {
      setLoading(true);
      return () => {
        mounted = false;
      };
    }

    if (!isAuthenticated || !hasUser) {
      setFlags(DEFAULT_FLAGS);
      setMaintenance({});
      setLastLoadedAt(0);
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    async function load(): Promise<void> {
      setLoading(true);
      try {
        const response = await fetchActiveOrganizationFeatureFlags();
        if (!mounted) return;
        setFlags({
          testcase_generation: response?.features?.testcase_generation !== false,
          test_execution: !!response?.features?.test_execution,
          synthetic_data: !!response?.features?.synthetic_data,
          byo_key: !!response?.features?.byo_key,
          data_validation_agent: !!response?.features?.data_validation_agent,
          exploratory_agent: !!response?.features?.exploratory_agent,
          ritz_reservation_demo: !!response?.features?.ritz_reservation_demo,
        });
        setMaintenance((response?.maintenance || {}) as ServiceMaintenanceMap);
      } catch {
        if (!mounted) return;
        setFlags(DEFAULT_FLAGS);
        setMaintenance({});
      } finally {
        if (mounted) {
          setLastLoadedAt(Date.now());
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [activeOrganizationId, authLoading, hasUser, isAuthenticated, refreshVersion, userId]);

  /*
   * Load maintenance state on auth/org changes and explicit refresh only.
   * Opening the native file picker causes a browser blur/focus cycle; refreshing
   * maintenance on focus can swap an active workspace to a maintenance page while
   * the user is selecting a file. Operators still get updated maintenance state
   * after a manual page reload or from callers that explicitly invoke refresh().
   */

  return useMemo(
    () => ({ flags, maintenance, loading, lastLoadedAt, refresh }),
    [flags, maintenance, loading, lastLoadedAt, refresh],
  );
}
